import React, { useMemo, useCallback, useState, useEffect } from 'react';

import { generatePath, useHistory } from 'react-router';
import { getRouteByName } from 'app/routes';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import {
  TablePaginatedV8,
  order2Sorting,
  CellCenterV8,
  CellDateTimeV8,
  EyeColumnV8,
} from 'motion-components';
import { useLocation } from 'react-router-dom';
import { getAuditTableDisplayName, getAuditTableFieldDisplayName } from '../audit-trail-dictionary';
import { auditTypeColor, useFieldValue2StringExtra } from '../common';
import { useTranslation } from 'react-i18next';

/* ----------- Grid ------------------ */

export const DataGrid = ({
  data,
  dataCount,
  initPageIndex,
  onPageChange,
  loading,
  handleExport,
  initOrderBy,
  onOrderChange,
}) => {
  const { t } = useTranslation();
  const auditTrailDb = null;
  const { appStore, tableStore } = useRootStore();

  const location = useLocation<any>();
  const history = useHistory();
  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('AuditTrail'), { id: row.id }),
        state: {
          pathname: location.pathname,
          ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'auditDate',
        header: t('Date of Change'),
        columnName: t('Date of Change'),
        accessorKey: 'auditDate',
        orderByField: 'auditDate',
        cell: CellDateTimeV8,
        size: 12,
      },
      {
        id: 'user',
        header: t('User Name'),
        columnName: t('User Name'),
        accessorKey: 'user',
        orderByField: 'user',
        size: 12,
      },
      {
        id: 'auditType',
        header: t('Action'),
        columnName: t('Action'),
        accessorKey: 'auditType',
        orderByField: 'auditType',
        cell: ({ row }) => {
          const auditType = row.original.auditType;
          const color = auditTypeColor(auditType);
          return <div style={{ color: color }}>{auditType}</div>;
        },
        size: 10,
      },
      {
        id: 'tableName',
        header: t('Element'),
        columnName: t('Element'),
        accessorKey: 'tableName',
        orderByField: 'tableName',
        cell: ({ row }) => {
          const tableName = row.original.tableName;
          const displayName = getAuditTableDisplayName(tableName);
          return (
            <div>
              <div>{displayName}</div>
              <div style={{ paddingTop: '4px', color: 'gray', fontFamily: 'monospace' }}>
                {tableName}
              </div>
            </div>
          );
        },
        size: 20,
      },
      {
        id: 'tableRowId',
        header: t('Id of Element'),
        columnName: t('Id of Element'),
        accessorKey: 'tableRowId',
        orderByField: 'tableRowId',
        cell: CellCenterV8,
        size: 8,
      },
      {
        id: 'previousValues1',
        header: t('Field Values'),
        columnName: t('Field Values'),
        accessorKey: 'previousValues',
        enableSorting: false,
        cell: ({ row }) => {
          const previousValues = JSON.parse(row.original.previousValues);
          const currentValues = JSON.parse(row.original.currentValues);
          return (
            <AuditRecordValues
              tableName={row.original.tableName}
              auditType={row.original.auditType}
              previousValues={previousValues}
              currentValues={currentValues}
              auditTrailDb={auditTrailDb}
            ></AuditRecordValues>
          );
        },
        size: 40,
      },
    ],
    [rowClicked, t],
  );

  return (
    <LoadingOverlay active={loading} spinner text={t('Please wait...')}>
      {data ? (
        <TablePaginatedV8
          loading={loading}
          id="audit-trails-grid"
          appStore={appStore}
          tableStore={tableStore}
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(_sort, _col, orderBy) => {
            onOrderChange(orderBy);
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
};

const AuditRecordValues = ({
  tableName,
  auditType,
  previousValues,
  currentValues,
  auditTrailDb,
}) => {
  if (currentValues === null) {
    return <div> null</div>;
  } else if (auditType === 'update') {
    const ret = (
      <div style={{ maxHeight: '120px', overflowY: 'scroll' }}>
        {Object.keys(currentValues).map((key, index) => {
          return (
            <FieldRow4Update
              key={index}
              tableName={tableName}
              fieldName={key}
              index={index}
              previousValues={previousValues}
              currentValues={currentValues}
            />
          );
        })}
      </div>
    );
    return ret;
  } else {
    // delete or create
    let values = {};
    if (auditType === 'create') values = currentValues;
    else values = previousValues;
    if (values === null) {
      return <div> null</div>;
    } else {
      // return (
      //   <div style={{ maxHeight: '120px', overflowY: 'scroll' }}>
      //     {Object.keys(values).map((key, index) => {
      //       const displayKey = getAuditTableFieldDisplayName(tableName, key);
      //       const displayValue = fieldValue2String(tableName, key, values[key]);
      //       return (
      //         <div key={index}>
      //           <span style={{ fontFamily: 'monospace', color: 'gray' }}>{displayKey}</span>: {displayValue}
      //         </div>
      //       );
      return (
        <div style={{ maxHeight: '120px', overflowY: 'scroll' }}>
          {Object.keys(values).map((key, index) => {
            return (
              <FieldRow4CreateOrDelete
                key={index}
                tableName={tableName}
                fieldName={key}
                index={index}
                currentValues={auditType === 'create' ? currentValues : previousValues}
              />
            );
          })}
        </div>
      );
    }
  }
};

const FieldRow4Update = ({ tableName, fieldName, index, previousValues, currentValues }) => {
  const { fieldValue2StringExtra } = useFieldValue2StringExtra();
  const displayKey = getAuditTableFieldDisplayName(tableName, fieldName);

  const [prvValue, setPrvValue] = useState<string>();

  const [curValue, setCurValue] = useState<string>();
  useEffect(() => {
    async function get() {
      const lcurValue = await fieldValue2StringExtra(
        tableName,
        fieldName,
        currentValues[fieldName],
      );
      setCurValue(lcurValue);
      const lprvValue = await fieldValue2StringExtra(
        tableName,
        fieldName,
        previousValues[fieldName],
      );
      setPrvValue(lprvValue);
    }
    get();
  }, [currentValues, fieldName, fieldValue2StringExtra, previousValues, tableName]);

  if (prvValue === curValue) {
    return (
      <div key={index}>
        <span style={{ fontFamily: 'monospace', color: 'gray' }}>{displayKey}</span>: {prvValue}
      </div>
    );
  } else {
    return (
      <div key={index}>
        <strong>
          <span style={{ fontFamily: 'monospace', color: 'gray' }}>{displayKey}</span>
        </strong>
        : {prvValue} {'->'} <strong>{curValue}</strong>
      </div>
    );
  }
};

const FieldRow4CreateOrDelete = ({ tableName, fieldName, index, currentValues }) => {
  const { fieldValue2StringExtra } = useFieldValue2StringExtra();
  const displayKey = getAuditTableFieldDisplayName(tableName, fieldName);

  const [curValue, setCurValue] = useState<string>();
  useEffect(() => {
    async function get() {
      const lcurValue = await fieldValue2StringExtra(
        tableName,
        fieldName,
        currentValues[fieldName],
      );
      setCurValue(lcurValue);
    }
    get();
  }, [currentValues, fieldName, fieldValue2StringExtra, tableName]);

  return (
    <div key={index}>
      <span style={{ fontFamily: 'monospace', color: 'gray' }}>{displayKey}</span>: {curValue}
    </div>
  );
};
